import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  useTheme,
  alpha
} from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import AppButton from '../buttons/AppButton'

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  description?: string
  severity?: 'error' | 'warning' | 'info'
  confirmText?: string
  cancelText?: string
  loading?: boolean
}

interface ConfirmTheme {
  palette: {
    mode: 'dark' | 'light'
    divider: string
    error: { main: string }
    warning: { main: string }
    info: { main: string }
  }
  appRadius: { md: string; lg: string }
  shadows: string[]
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  severity = 'warning',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false
}): React.JSX.Element => {
  const theme = useTheme() as unknown as ConfirmTheme

  const config = {
    error: {
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 48 }} />,
      color: theme.palette.error.main,
      bgColor: alpha(theme.palette.error.main, 0.1),
      btnColor: 'error' as const
    },
    warning: {
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 48 }} />,
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.1),
      btnColor: 'warning' as const
    },
    info: {
      icon: <HelpOutlineOutlinedIcon sx={{ fontSize: 48 }} />,
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1),
      btnColor: 'info' as const
    }
  }

  const currentConfig = config[severity] || config.warning

  return (
    <Dialog
      open={open}
      // Disable closing by clicking outside or pressing Escape while loading
      onClose={loading ? undefined : onClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: theme.appRadius.lg,
          p: 2,
          minWidth: 320,
          maxWidth: 450,
          textAlign: 'center',
          boxShadow: theme.shadows[24]
        }
      }}
    >
      {/* --- ICON HEADER --- */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: currentConfig.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: currentConfig.color
          }}
        >
          {currentConfig.icon}
        </Box>
      </Box>

      {/* --- CONTENT --- */}
      <DialogTitle sx={{ p: 0, mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </DialogContent>

      {/* --- ACTIONS --- */}
      <DialogActions sx={{ justifyContent: 'center', gap: 2, p: 0 }}>
        <AppButton
          onClick={onClose}
          variant="outlined"
          pill
          color="inherit"
          disabled={loading}
          sx={{
            px: 3,
            color: 'text.secondary',
            borderColor: theme.palette.divider,
            '&:hover': {
              borderColor: 'text.primary',
              bgcolor: 'action.hover'
            }
          }}
        >
          {cancelText}
        </AppButton>
        <AppButton
          onClick={onConfirm}
          variant="contained"
          pill
          color={currentConfig.btnColor}
          isLoading={loading}
          disabled={loading}
          autoFocus
          sx={{
            px: 3,
            fontWeight: 600,
            boxShadow: theme.shadows[4]
          }}
        >
          {confirmText}
        </AppButton>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDialog
